'use client';
import React, { useState, useRef, useEffect } from 'react';
import styles from '../styles/travelDestination.module.css';

export default function TravelDestinationSelect({
  value = [],
  onChange,
  options,
  placeholder,
  error = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClickOutside = event => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm('');
      // Remove focus from search input when clicking outside
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = option => {
    if (value.includes(option.value)) {
      // Remove if already selected
      onChange(value.filter(v => v !== option.value));
    } else {
      // Add to selection
      onChange([...value, option.value]);
    }
    setSearchTerm('');
    setIsOpen(false);
    // After selection, keep the dropdown closed until the user clicks the input again.
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  const handleRemove = (valueToRemove, e) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== valueToRemove));
    // Keep focus on input after removal
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = e => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputKeyDown = e => {
    if (e.key !== 'Enter' || filteredOptions.length !== 1) return;

    e.preventDefault();
    handleSelect(filteredOptions[0]);
  };

  const selectedOptions = options.filter(opt => value.includes(opt.value));

  // Show input value when searching or when open, otherwise empty
  const inputValue = isOpen || searchTerm ? searchTerm : '';
  // Show tags when there are selections and not actively searching
  const showTags = value.length > 0 && (!isOpen || !searchTerm);
  // Determine placeholder text
  const placeholderText =
    value.length === 0 && !isOpen ? placeholder : 'Ieškoti šalies...';

  return (
    <div ref={containerRef} className={styles.container}>
      <div
        className={`${styles.inputWrapper} ${error ? styles.inputError : ''}`}
      >
        <svg
          className={styles.searchIcon}
          width='20'
          height='20'
          viewBox='0 0 20 20'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M9.58334 17.5C13.9556 17.5 17.5 13.9556 17.5 9.58334C17.5 5.21112 13.9556 1.66667 9.58334 1.66667C5.21112 1.66667 1.66667 5.21112 1.66667 9.58334C1.66667 13.9556 5.21112 17.5 9.58334 17.5Z'
            stroke='#666'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M18.3333 18.3333L16.6667 16.6667'
            stroke='#666'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
        {showTags && (
          <div className={styles.selectedContainer}>
            {selectedOptions.map(option => (
              <div key={option.value} className={styles.tag}>
                <span className={styles.tagText}>{option.label}</span>
                <button
                  className={styles.removeButton}
                  onClick={e => handleRemove(option.value, e)}
                >
                  <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
                    <path
                      d='M9 3L3 9M3 3L9 9'
                      stroke='#666'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
        <input
          ref={searchInputRef}
          type='text'
          className={styles.searchInput}
          placeholder={placeholderText}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          onClick={e => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        />
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.optionsList}>
            {filteredOptions.map(option => {
              const isSelected = value.includes(option.value);
              return (
                <div
                  key={option.value}
                  className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                  {isSelected && (
                    <svg
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M13.3334 4L6.00002 11.3333L2.66669 8'
                        stroke='#444CE7'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
